import { useMemo } from "react";
import { format, parseISO } from "date-fns";
import { Edit2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

const ListView = ({ entries, categories, onEditEntry, onDeleteEntry }) => {
  // Group entries by date, most recent first
  const groupedEntries = useMemo(() => {
    const groups = {};
    
    entries.forEach(entry => {
      if (!groups[entry.date]) {
        groups[entry.date] = [];
      }
      groups[entry.date].push(entry);
    });

    // Sort dates descending
    return Object.entries(groups)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([date, entries]) => ({
        date,
        entries
      }));
  }, [entries]);

  if (entries.length === 0) {
    return (
      <div className="text-center py-20" data-testid="list-view-empty">
        <p className="text-muted-foreground text-lg">No wins recorded yet.</p>
        <p className="text-muted-foreground text-sm mt-2">
          Click on any day in the calendar to add your first win!
        </p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[calc(100vh-300px)]" data-testid="list-view">
      <div className="space-y-6 pr-4">
        {groupedEntries.map(({ date, entries: dayEntries }) => (
          <div key={date}>
            {/* Date header */}
            <div className="flex items-center gap-3 mb-3">
              <div className="h-px flex-1 bg-border" />
              <span className="text-sm font-medium text-muted-foreground">
                {format(parseISO(date), "EEEE, MMMM d, yyyy")}
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>

            {/* Entries for this date */}
            <div className="space-y-3">
              {dayEntries.map(entry => {
                const category = categories[entry.category];
                return (
                  <div 
                    key={entry.entry_id}
                    className="entry-card group"
                    data-testid={`entry-${entry.entry_id}`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Category indicator */}
                      <div 
                        className="category-dot mt-1.5 flex-shrink-0"
                        style={{ backgroundColor: category?.color || '#888' }}
                      />
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className="text-foreground">{entry.text}</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {category?.name || entry.category}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => onEditEntry(entry)}
                          data-testid={`edit-${entry.entry_id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => onDeleteEntry(entry.entry_id)}
                          data-testid={`delete-${entry.entry_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </ScrollArea>
  );
};

export default ListView;
